/**
 * 客户 H5/PWA 会话存储（Issue #198）。
 *
 * 设计目标：
 *   - access_token 仅驻留进程内存，不持久化。
 *   - refresh_token / orgId / tenantId 暂存于 sessionStorage（同标签页内存活、
 *     标签页关闭即清除）。当 backend 完成 HttpOnly Cookie + CSRF 收敛后，
 *     由统一的 token storage 工程替换。
 *   - 任何时候都不允许将 refresh_token 写入 localStorage 或 IndexedDB；
 *     第一版客户 PWA 不做"记住我"，强制每次会话重新登录。
 *
 * TECH_DEBT(token-storage-hardening):
 *   - 与 client-app 一致的阶段性方案，等待 HttpOnly Cookie + CSRF 上线后统一收敛。
 */

const REFRESH_TOKEN_KEY = 'jzc.web.refresh_token.v1';
const ORG_ID_KEY = 'jzc.web.org_id.v1';
const TENANT_ID_KEY = 'jzc.web.tenant_id.v1';

let accessTokenInMemory: string | null = null;

export interface WebSessionStore {
  getAccessToken(): string | null;
  setAccessToken(token: string | null): void;

  loadRefreshToken(): string | null;
  saveRefreshToken(token: string): void;
  clearRefreshToken(): void;

  loadOrgId(): string | null;
  saveOrgId(orgId: string): void;
  clearOrgId(): void;

  loadTenantId(): string | null;
  saveTenantId(tenantId: string): void;
  clearTenantId(): void;

  clearAll(): void;
}

function getSessionStorage(): Storage | null {
  if (typeof window === 'undefined') return null;
  try {
    return window.sessionStorage;
  } catch {
    return null;
  }
}

function readKey(key: string): string | null {
  const storage = getSessionStorage();
  if (!storage) return null;
  try {
    return storage.getItem(key);
  } catch {
    return null;
  }
}

function writeKey(key: string, value: string): void {
  const storage = getSessionStorage();
  if (!storage) return;
  try {
    storage.setItem(key, value);
  } catch {
    // sessionStorage may be unavailable (quota, private mode). Login will
    // still set access token in memory; refresh after access expiry will fail
    // and the user is asked to log in again — never a silent retention bug.
  }
}

function removeKey(key: string): void {
  const storage = getSessionStorage();
  if (!storage) return;
  try {
    storage.removeItem(key);
  } catch {
    // ignore — best effort cleanup
  }
}

export const webSessionStore: WebSessionStore = {
  getAccessToken(): string | null {
    return accessTokenInMemory;
  },
  setAccessToken(token: string | null): void {
    accessTokenInMemory = token;
  },
  loadRefreshToken(): string | null {
    return readKey(REFRESH_TOKEN_KEY);
  },
  saveRefreshToken(token: string): void {
    if (!token) {
      throw new Error('refresh_token 不能为空');
    }
    writeKey(REFRESH_TOKEN_KEY, token);
  },
  clearRefreshToken(): void {
    removeKey(REFRESH_TOKEN_KEY);
  },
  loadOrgId(): string | null {
    return readKey(ORG_ID_KEY);
  },
  saveOrgId(orgId: string): void {
    if (!orgId) {
      throw new Error('orgId 不能为空');
    }
    writeKey(ORG_ID_KEY, orgId);
  },
  clearOrgId(): void {
    removeKey(ORG_ID_KEY);
  },
  loadTenantId(): string | null {
    return readKey(TENANT_ID_KEY);
  },
  saveTenantId(tenantId: string): void {
    if (!tenantId) {
      throw new Error('tenantId 不能为空');
    }
    writeKey(TENANT_ID_KEY, tenantId);
  },
  clearTenantId(): void {
    removeKey(TENANT_ID_KEY);
  },
  clearAll(): void {
    accessTokenInMemory = null;
    removeKey(REFRESH_TOKEN_KEY);
    removeKey(ORG_ID_KEY);
    removeKey(TENANT_ID_KEY);
  },
};

/**
 * Test-only reset. Production code must not call this.
 */
export function __unsafeResetSessionStoreForTests(): void {
  accessTokenInMemory = null;
  removeKey(REFRESH_TOKEN_KEY);
  removeKey(ORG_ID_KEY);
  removeKey(TENANT_ID_KEY);
}
